<template>
  <div class="min-h-screen bg-white relative flex flex-col">
    <GradientHalo />

    <!-- Header -->
    <header class="relative flex items-center justify-between px-6 md:px-10 pt-6 md:pt-8">
      <div class="flex items-center gap-3">
        <img class="h-8 w-auto" src="@/assets/large_logo_estaly_black.png" alt="Estaly" />
        <span
          class="inline-flex items-center rounded-full bg-primary-light text-primary-dark text-xs font-semibold px-3 py-1"
        >
          {{ t("insurer_space") }}
        </span>
      </div>
    </header>

    <!-- Main -->
    <main class="relative flex-1 flex items-center justify-center px-4 py-8 md:py-0">
      <ModernCard>
        <h1 class="text-2xl font-bold text-gray-900 leading-tight">{{ t("title") }}</h1>
        <p class="mt-2 text-sm text-gray-500 leading-relaxed">{{ t("subtitle") }}</p>

        <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div" class="mt-8">
          <form @submit="handleSubmit($event, handleSignin)" novalidate>
            <ModernFormField
              id="email-address"
              name="email"
              type="email"
              autocomplete="email"
              v-model="user.email"
              :label="t('email_label')"
              :placeholder="t('email_placeholder')"
            />

            <ModernFormField
              id="password"
              name="password"
              :type="passwordVisible ? 'text' : 'password'"
              autocomplete="current-password"
              v-model="user.password"
              wrapper-class="mb-4"
              :label="t('password_label')"
              :placeholder="t('password_placeholder')"
            >
              <template #suffix>
                <PasswordToggleButton
                  :visible="passwordVisible"
                  :show-label="t('show_password')"
                  :hide-label="t('hide_password')"
                  @toggle="passwordVisible = !passwordVisible"
                />
              </template>
            </ModernFormField>

            <!-- Forgot password -->
            <div class="flex justify-end mb-6">
              <router-link
                to="/forgot-password"
                class="text-sm text-gray-500 underline hover:text-gray-900 transition-colors"
              >
                {{ t("forgot_password") }}
              </router-link>
            </div>

            <!-- Submit button -->
            <ModernButton
              :loading="isLoading"
              :success="isSuccess"
              :loading-text="t('loading')"
              :success-text="t('success')"
            >
              {{ t("submit") }}
            </ModernButton>
          </form>
        </VeeForm>
      </ModernCard>
    </main>

    <!-- Footer -->
    <footer class="relative text-center py-6 text-sm text-gray-400">
      {{ t("need_help") }}
      <a href="mailto:contact@estaly.co" class="text-gray-500 underline hover:text-gray-900 transition-colors">
        {{ t("contact_us") }}
      </a>
    </footer>
  </div>
</template>

<script>
import { Form as VeeForm } from "vee-validate";
import * as yup from "yup";
import { mapActions, mapState, mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import { ModernButton, ModernFormField, ModernCard, GradientHalo, PasswordToggleButton, User } from "@estaly/ui";

export default {
  components: {
    VeeForm,
    ModernButton,
    ModernFormField,
    ModernCard,
    GradientHalo,
    PasswordToggleButton,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      user: new User("", ""),
      passwordVisible: false,
      isLoading: false,
      isSuccess: false,
    };
  },

  computed: {
    ...mapState("auth", ["signedIn"]),
    ...mapGetters("auth", ["isInsurerManager"]),
    schema() {
      return yup.object({
        email: yup.string().required(this.t("email_required")).email(this.t("email_invalid")),
        password: yup.string().required(this.t("password_required")).min(8, this.t("password_too_short")),
      });
    },
  },

  created() {
    if (this.signedIn) this.$router.push("/home");
  },

  methods: {
    ...mapActions("auth", ["signin", "signout"]),
    ...mapActions("notifications", ["notify"]),

    async handleSignin() {
      this.isLoading = true;

      try {
        const response = await this.signin(this.user);

        if (!response?.success) return;

        if (!this.isInsurerManager) {
          this.notify({ type: "error", category: "simple", title: this.t("wrong_url") });
          await this.signout();
          return;
        }

        this.isSuccess = true;
        setTimeout(() => this.$router.push("/home"), 600);
      } catch (error) {
        this.notify({
          type: "error",
          category: "simple",
          title: this.t("errors.Unexpected"),
          text: error.response?.data?.error || error?.message,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<i18n src="./Signin.i18n.json"></i18n>
